import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../../models/NameUtils';
import { useUserQuery } from '../../../../../queries';
import { SelectEmployeeCombobox } from '../../../../components/SendFromDropdown';
import { Label } from '../../../../components-v2/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { styleSheet } from '../styles';
import { EventRegistrationFormData, getConfirmationSenderEditor } from '../utils';

const LevitateOption = 'levitate';
const TeamMemberOption = 'teamMember';
const SenderInputId = 'confirmation-email-sender-input';

interface Props {
	eventData?: Api.IEventRegistrationSurvey;
	impersonationContext?: Api.IImpersonationContext;
	account?: Api.IAccount;
	isClone?: boolean;
}

export const ConfirmationEmailSender: React.FC<Props> = ({ eventData, impersonationContext, account, isClone }) => {
	const userSession = useUserSession();
	const { control, setValue, watch } = useFormContext<EventRegistrationFormData>();
	const senderId = watch('confirmationEmailSenderId');
	const [isPickingTeamMember, setIsPickingTeamMember] = React.useState(false);

	const { editorId, isImpersonating, canChooseAnyone } = getConfirmationSenderEditor(
		userSession.user,
		!!userSession.isAdmin,
		impersonationContext,
		account
	);
	const isOtherUser = !!senderId && senderId !== editorId;
	const sessionUserIsSender = !isImpersonating && !!senderId && senderId === userSession.user?.id;

	// GET user/{id} for someone else is admin-only, so a non-admin can only look themselves up
	const senderQuery = useUserQuery({
		id: senderId ?? '',
		impersonationContext,
		enabled: !!senderId && !sessionUserIsSender && (canChooseAnyone || senderId === editorId),
		meta: { toastErrorMessage: 'Unable to load the confirmation email sender' },
	});
	const sender = sessionUserIsSender ? userSession.user : senderQuery.data;
	const senderNeedsConnection = !!sender && (!sender.emailCalendarTokenExists || !!sender.reconnectRequired);

	const defaultSenderId = canChooseAnyone ? (!isClone && eventData?.creator?.id) || editorId : editorId;
	const showTeamMember = !!senderId || isPickingTeamMember;

	return (
		<>
			<FormFieldWrapper
				name='confirmationEmailSenderId'
				control={control}
				title='Confirmation Email Sender'
				description='Who the registration confirmation email comes from. Replies go to the sender.'
				textWrapperStyles={[styleSheet.confirmationSenderText]}
				controllerWrapperStyles={[styleSheet.confirmationSenderRow]}
			>
				{({ value, onChange }) => (
					<Select
						value={showTeamMember ? TeamMemberOption : LevitateOption}
						onValueChange={val => {
							const isTeamMember = val === TeamMemberOption;
							setIsPickingTeamMember(isTeamMember);
							onChange(isTeamMember ? (value ?? defaultSenderId ?? null) : null);
						}}
					>
						<SelectTrigger>
							<SelectValue>{showTeamMember ? 'A team member' : 'Levitate (no-reply)'}</SelectValue>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={LevitateOption}>Levitate (no-reply)</SelectItem>
							<SelectItem value={TeamMemberOption}>A team member</SelectItem>
						</SelectContent>
					</Select>
				)}
			</FormFieldWrapper>
			{showTeamMember ? (
				<div className={css(styleSheet.guestSubSection)}>
					{canChooseAnyone ? (
						<>
							<Label htmlFor={SenderInputId}>Sender</Label>
							<SelectEmployeeCombobox
								inputId={SenderInputId}
								selectedUserId={senderId ?? undefined}
								onSelect={(user: Api.IUser) => setValue('confirmationEmailSenderId', user.id, { shouldDirty: true })}
							/>
						</>
					) : (
						<p className={css(styleSheet.confirmationSenderName)}>
							{isOtherUser
								? 'Another team member (only an admin can change this)'
								: sender
									? `You (${getDisplayName(sender)})`
									: 'You'}
						</p>
					)}
					{senderQuery.isError ? (
						<p className={css(styleSheet.confirmationSenderWarning)}>
							The selected sender could not be loaded. Choose the sender again before saving.
						</p>
					) : null}
					{senderNeedsConnection ? (
						<p className={css(styleSheet.confirmationSenderWarning)}>
							{isOtherUser ? 'This person' : 'You'} must connect an email account before confirmations can be sent from
							it. Until then, they will be sent from Levitate with replies going to {isOtherUser ? 'them' : 'you'}.
						</p>
					) : null}
				</div>
			) : null}
		</>
	);
};
